// src/pages/shrines/ShrineOfImamHussain.jsx
import React from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";

const ShrineOfImamHussain = () => {
  // Static image URLs
  const shrineImage = "https://ziyaratguide.com/wp-content/uploads/2025/09/ImamHussain.jpg";
  const interiorImage = "https://ziyaratguide.com/wp-content/uploads/2025/09/ImamHussain1.avif";
  const beynAlHaramayn = "https://ziyaratguide.com/wp-content/uploads/2025/09/ImamHussain2.avif";
  const karbalaLandscape = "https://ziyaratguide.com/wp-content/uploads/2025/09/ImamHussain3.avif";
  const pilgrims = "https://ziyaratguide.com/wp-content/uploads/2025/09/ImamHussain4.avif";

  return (
    <>
      <Helmet>
        <title>Shrine of Imam Hussain (AS) – Karbala | Ziyarat Guide</title>
        <meta
          name="description"
          content="Explore the Holy Shrine of Imam Hussain (AS) in Karbala, Iraq. Learn its history, spiritual significance, and why it remains a symbol of justice and humanity."
        />
        <meta property="og:title" content="Shrine of Imam Hussain (AS) – Karbala | Ziyarat Guide" />
        <meta
          property="og:description"
          content="The Holy Shrine of Imam Hussain (AS) in Karbala is a universal symbol of courage, justice, and sacrifice. Discover its history, architecture, and spiritual impact."
        />
        <meta property="og:image" content={shrineImage} />
        <meta property="og:type" content="article" />
        <meta name="twitter:card" content="summary_large_image" />
      </Helmet>

      <div className="imam-page">
        {/* Main content */}
        <div className="imam-content">
          <h1 className="page-title">The Holy Shrine of Imam Hussain</h1>

          <section className="content-section">
            <p>
              The Holy Shrine of Imam Hussain in Karbala, Iraq, is among the most significant
              religious and spiritual sites in the world, particularly for Muslims, and holds
              universal appeal as a symbol of courage, justice, and resistance against oppression
              and corruption. The shrine is the resting place of Imam Hussain Ibn Imam Ali (PBUH),
              the grandson of Prophet Muhammad (PBUH), who was martyred in the Battle of Karbala
              on the 10th of Muharram in 680 CE.
            </p>
            <img
              src={shrineImage}
              alt="The Holy Shrine of Imam Hussain in Karbala"
              className="imam-image"
            />
          </section>

          <section className="content-section">
            <h2>Architectural and Spiritual Grandeur</h2>
            <h3>Structure:</h3>
            <p>
              The shrine features majestic golden domes, intricately decorated walls, and stunning Islamic calligraphy.
            </p>
            <img
              src={interiorImage}
              alt="Interior view of Imam Hussain's shrine"
              className="imam-image"
            />
            <p>
              Inside, the Zarih (a latticed enclosure marking Imam Hussain’s grave) is adorned with gold and silver,
              symbolising reverence and devotion.
            </p>
            <h3>Beyn al-Haramayn:</h3>
            <p>
              The area between Imam Hussain’s shrine and Al-Abbas Shrine is a wide, beautifully lit pedestrian
              walkway where pilgrims gather, pray, and reflect.
            </p>
            <img
              src={beynAlHaramayn}
              alt="Beyn al-Haramayn, the area between Imam Hussain and Al-Abbas shrines"
              className="imam-image"
            />
            <h3>Courtyards and Prayer Halls:</h3>
            <p>
              The shrine has spacious courtyards and prayer halls where pilgrims recite prayers,
              engage in supplication, and seek spiritual solace.
            </p>
          </section>

          <section className="content-section">
            <h2>Historical and Religious Significance</h2>
            <h3>The Battle of Karbala</h3>
            <p>
              Imam Hussain (PBUH) and his 72 companions stood against the tyrannical & dictatorial
              rule of Yazid Ibn Muawiya, refusing to pledge allegiance to injustice and corruption.
              Their sacrifice represents an eternal stand for truth, dignity, and justice, transcending time and culture.
            </p>
            <img
              src={karbalaLandscape}
              alt="Karbala historical landscape"
              className="imam-image"
            />
            <h3>Imam Hussain’s Legacy</h3>
            <p>
              Imam Hussain’s martyrdom symbolises selflessness, sacrifice, and standing against
              oppression & corruption, making him a universal figure for all of humanity, not just Muslims.
            </p>
            <p>
              His mission was to uphold the principles of freedom, justice, and human rights,
              values that resonate with people from all walks of life.
            </p>
          </section>

          <section className="content-section">
            <h2>How It Symbolises Humanity</h2>
            <h3>A Symbol of Justice and Resistance</h3>
            <p>
              Imam Hussain’s refusal to submit to tyranny embodies the principle of standing firm
              against oppression, regardless of the odds. His stance inspires people to fight for justice
              and to resist any form of oppression in their personal and collective lives.
            </p>
            <h3>A Beacon of Compassion and Humanity</h3>
            <p>
              The story of Karbala emphasises the importance of caring for the weak and marginalised.
              Imam Hussain’s camp included women, children, and the elderly, showing that compassion
              and family values are central to his message.
            </p>
            <h3>A Lesson in Sacrifice</h3>
            <p>
              The ultimate sacrifice of Imam Hussain and his companions teaches the value of prioritising
              the greater good over personal comfort or gain. It inspires people to uphold their values,
              even in the face of hardship and personal loss.
            </p>
            <h3>A Call for Unity and Peace</h3>
            <p>
              Imam Hussain’s message is not confined to a single religion or sect. His struggle was
              for universal principles, making the shrine a site of unity and reflection for people
              from diverse backgrounds. The shrine promotes interfaith dialogue and understanding,
              as pilgrims from various religions and sects visit to pay their respects.
            </p>
          </section>

          <section className="content-section">
            <h2>Spiritual Impact on Pilgrims</h2>
            <h3>Ziyarat (Pilgrimage)</h3>
            <p>
              Reciting Ziyarat prayers at the shrine connects pilgrims with Imam Hussain’s mission and legacy.
              It is a way of reaffirming commitment to justice, truth, and selflessness.
            </p>
            <h3>Renewal of Faith and Purpose</h3>
            <p>
              Pilgrims often describe a renewed sense of purpose, inspired by Imam Hussain’s unwavering dedication
              to God and humanity. Families bring their children to teach them about these values,
              ensuring the legacy continues.
            </p>
            <img
              src={pilgrims}
              alt="Pilgrims walking towards the holy shrine"
              className="imam-image"
            />
            <h3>Acts of Charity</h3>
            <p>
              Many visitors engage in charitable acts, such as feeding the poor or helping fellow pilgrims,
              embodying the shrine’s spirit of compassion and generosity.
            </p>
          </section>

          <section className="content-section">
            <h2>Why It Is Universally Relevant</h2>
            <p>The timeless message of Imam Hussain resonates deeply with various groups globally.</p>
            <h3>Human Rights Activists</h3>
            <p>
              The message of Karbala inspires movements against social and political injustice,
              making Imam Hussain a figure admired by activists across faiths.
            </p>
            <h3>Writers and Thinkers</h3>
            <p>
              Philosophers, poets, and leaders have drawn inspiration from Hussain’s stand.
              For example, Mahatma Gandhi said, “I learned from Hussain how to achieve victory while being oppressed.”
            </p>
            <h3>Spiritual Seekers</h3>
            <p>
              The shrine serves as a spiritual sanctuary, where visitors reflect on life’s higher
              purpose and the importance of moral courage.
            </p>
          </section>

          <section className="content-section">
            <h2>Visiting the Shrine: A Family and Community Experience</h2>
            <h3>For families:</h3>
            <ul>
              <li>
                <strong>Educating Children:</strong> Visiting the shrine provides an opportunity
                to teach children about moral values, history, and the importance of standing for justice.
              </li>
              <li>
                <strong>Strengthening Bonds:</strong> The shared spiritual experience fosters
                family unity and a collective sense of purpose.
              </li>
            </ul>
            <h3>For communities:</h3>
            <ul>
              <li>
                <strong>Unity and Solidarity:</strong> The shrine attracts millions of pilgrims during
                Muharram, Arbaeen, and throughout the year, fostering a global sense of solidarity
                and shared values.
              </li>
            </ul>
          </section>

          <section className="content-section">
            <h2>Key Events at the Shrine</h2>
            <h3>Arbaeen Pilgrimage:</h3>
            <p>
              Millions walk to the shrine from around the world to commemorate the 40th day after Imam Hussain’s martyrdom.
            </p>
            <h3>Ashura Mourning:</h3>
            <p>
              Rituals, processions, and prayers are held annually to remember the events of Karbala.
            </p>
          </section>

          <section className="content-section">
            <p>
              The Holy Shrine of Imam Hussain is not just a physical structure but a profound symbol of human values.
              It serves as a timeless reminder that the struggle for justice, compassion, and truth is universal,
              inspiring individuals and communities across generations.
            </p>
          </section>
        </div>

        {/* Sidebar */}
        <aside className="imam-sidebar">
          <h2>Other Shrines</h2>
          <ul>
            <li><Link to="/shrines/shrine-of-imam-hussain">Imam Hussain (PBUH) Shrine</Link></li>
            <li><Link to="/shrines/al-abbas-shrine">Al Abbas (PBUH) Shrine</Link></li>
            <li><Link to="/shrines/al-mukhayam-shrine">Al-Mukhayam Shrine</Link></li>
            <li><Link to="/shrines/shrine-of-two-sons-muslim-ibn-aqeel">Sons of Muslim ibn Aqeel</Link></li>
          </ul>
        </aside>
      </div>
    </>
  );
};

export default ShrineOfImamHussain;
